import { useState, useEffect } from "react";
import { BACKEND_URL } from "../config/supabase.js";

// ─── MOP (Mode-of-Payment) config ─────────────────────────────────────────────
// UPI / Card / Netbanking each price the checkout independently. These are the rates from
// the confirmed rate card — used until (or if) the backend's live /api/settings config
// disagrees, and always used if that fetch fails. See Bloom/checkout-mop-plan.md (vault).
export const MOP_DEFAULTS = {
  methods: {
    upi:        { label: "UPI",        gatewayFactor: 0.9746, platformPct: 0.0254 },
    card:       { label: "Card",       gatewayFactor: 0.957,  platformPct: 0.03   },
    netbanking: { label: "Netbanking", gatewayFactor: 0.9746, platformPct: 0.0254 },
  },
};

// Turns a resolved `methods` map + a chosen method key into the {gatewayFactor, platformPct}
// shape quote() accepts as its second argument. A falsy/unknown key returns {} — quote() then
// falls through to its own defaults (today's flat 2.54% + tiered platform fee), so "no method
// chosen yet" or "MOP disabled" behave exactly like before this feature existed.
export function methodOpts(methods, key) {
  if (!key || !methods || !methods[key]) return {};
  const { gatewayFactor, platformPct } = methods[key];
  return { gatewayFactor, platformPct };
}

// Preview flag — same idea as razorpay.js's syncHostedPayFlag/?pay=modal: visiting the site
// once with ?mop=1 turns the selector on for THIS BROWSER (localStorage), ?mop=0 turns it back
// off, and no param leaves whatever was already set untouched. Lets the owner preview the feature
// on production before flipping the real `mop_enabled` admin switch for everyone.
export function syncMopPreviewFlag() {
  try {
    const p = new URLSearchParams(window.location.search).get("mop");
    if (p === "1") localStorage.setItem("bloom_mop", "1");
    else if (p === "0") localStorage.removeItem("bloom_mop");
  } catch { /* ignore */ }
}
function mopPreviewFlag() {
  try { return localStorage.getItem("bloom_mop") === "1"; } catch { return false; }
}

// React hook: { enabled, methods }. Fetches the live per-method rates from /api/settings ONCE
// on mount (server is authoritative — rates can change without a redeploy); any failure keeps
// MOP_DEFAULTS, silently. `enabled` is the real admin-wide switch OR the per-browser preview
// flag — either can turn the selector on for this viewer.
export function useMopConfig() {
  const [state, setState] = useState({ enabled: mopPreviewFlag(), methods: MOP_DEFAULTS.methods });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let methods = MOP_DEFAULTS.methods;
      let mopEnabled = false;
      try {
        const r = await fetch(`${BACKEND_URL}/api/settings`);
        if (r.ok) {
          const d = await r.json();
          mopEnabled = d.mop_enabled === true;
          const rates = d.method_rates;
          if (rates && typeof rates === "object") {
            const merged = {};
            for (const key of Object.keys(MOP_DEFAULTS.methods)) {
              const base = MOP_DEFAULTS.methods[key];
              const live = rates[key];
              merged[key] = (live && typeof live === "object")
                ? {
                    label: base.label,
                    gatewayFactor: typeof live.gateway_factor === "number" ? live.gateway_factor : base.gatewayFactor,
                    platformPct:   typeof live.bloom_pct === "number" ? live.bloom_pct : base.platformPct,
                  }
                : base;
            }
            methods = merged;
          }
        }
      } catch { /* network/parse failure — MOP_DEFAULTS stands, mopEnabled stays false from the fetch */ }
      if (!cancelled) setState({ enabled: mopEnabled || mopPreviewFlag(), methods });
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
